import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPartnerTickets } from "../../utility/partnerCheckout";
import { downloadPDF } from "../../utility/createDownloadTicket";

export default function PartnerTickets({ event, partnerId }) {
  const { t } = useTranslation("eventdesc");
  const [participants, setParticipants] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(null);
  const request = useRef(0);
  const downloadLock = useRef(false);
  const retryCursor = useRef(null);
  const invalidate = useCallback(() => { request.current++; }, []);
  const load = useCallback(async (after = null) => {
    const id = ++request.current;
    retryCursor.current = after;
    setLoading(true);
    setError("");
    try {
      const result = await getPartnerTickets({ eventId: event.id, partnerId, cursor: after });
      if (id !== request.current) return;
      setParticipants((current) => after ? [...current, ...result.participants.filter((entry) => !current.some((item) => item.id === entry.id))] : result.participants);
      setCursor(result.nextCursor);
    } catch (failure) {
      if (id === request.current) setError(failure.message || t("registration_failed"));
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [event.id, partnerId, t]);
  useEffect(() => {
    setParticipants([]);
    setCursor(null);
    load();
    return invalidate;
  }, [load, invalidate]);
  const download = async (participant) => {
    if (downloadLock.current) return;
    downloadLock.current = true;
    setDownloading(participant.id);
    setError("");
    try { await downloadPDF({ participant, event }); }
    catch (failure) { setError(failure.message || t("registration_failed")); }
    finally { setDownloading(null); downloadLock.current = false; }
  };
  const detail = (person, name) => (person.details || []).find((field) => field.name === name)?.value || "—";
  return <section className="partner-ticket-history" aria-label={t("partner_your_tickets")}>
    <div className="partner-history-heading"><h3>{t("partner_your_tickets")}</h3>
      <button type="button" disabled={loading} onClick={() => load()}>{t("partner_history_refresh")}</button></div>
    <p>{t("partner_history_description")}</p>
    {error && <div role="alert"><p>{error}</p><button type="button" disabled={loading} onClick={() => load(retryCursor.current)}>{t("retry")}</button></div>}
    <div className="partner-participants-table" role="region" tabIndex={0} aria-label={t("partner_your_tickets")}>
      <table><thead><tr><th>{t("firstname")}</th><th>{t("lastname")}</th><th>{t("email")}</th><th>{t("ticketName")}</th><th>{t("ticketOptions")}</th><th>{t("quantity")}</th><th>{t("actions")}</th></tr></thead>
        <tbody>{participants.map((participant) => <tr key={participant.id}>
          <td>{detail(participant, "Firstname")}</td><td>{detail(participant, "Name")}</td><td>{detail(participant, "Email")}</td>
          <td>{participant.tickets.map((line, index) => <div key={index}>{line.name || line.title || line.ticketName || line.ticketId}</div>)}</td>
          <td>{participant.tickets.map((line, index) => <div key={index}>{line.optionSelections?.map((option) => [option.groupName, option.valueLabel || option.label].filter(Boolean).join(": ")).filter(Boolean).join(", ") || "—"}</div>)}</td>
          <td>{participant.tickets.reduce((sum, line) => sum + Number(line.number || 0), 0)}</td>
          <td><button type="button" disabled={downloading !== null} onClick={() => download(participant)}
            aria-label={`${t("download_tickets_here")} — ${detail(participant, "Firstname")} ${detail(participant, "Name")}`}>
            {t(downloading === participant.id ? "partner_history_downloading" : "download_tickets_here")}</button></td>
        </tr>)}
        {!participants.length && !loading && !error && <tr><td colSpan={7}>{t("partner_history_empty")}</td></tr>}</tbody>
      </table>
    </div>
    {loading && <p role="status">{t("partner_history_loading")}</p>}
    {cursor && !error && <button type="button" disabled={loading} onClick={() => load(cursor)}>{t("partner_history_more")}</button>}
  </section>;
}
